import { useRef, useState, useEffect, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import { DgaIcon } from "platformscode-new-react";

// ──────────────────────────────────────────────────────────────
// SUBMENU LINK
// ──────────────────────────────────────────────────────────────
const SubmenuLink = memo(({ item, onClick }) => {
  const isExternal =
    item.external ||
    (item.href && (item.href.startsWith("http://") || item.href.startsWith("https://")));

  const content = (
    <div className="submenu__link-content">
      <DgaIcon icon={item.icon} color="#000000" size={24} type="rounded" variant="stroke" />
      <span>{item.label}</span>
    </div>
  );

  if (isExternal) {
    return (
      <li>
        <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={onClick} className="sub-link sub-menu__link">
          {content}
        </a>
      </li>
    );
  }

  return (
    <li>
      <Link to={item.href} onClick={onClick} className="sub-link sub-menu__link">
        {content}
      </Link>
    </li>
  );
});
SubmenuLink.displayName = "SubmenuLink";

// ──────────────────────────────────────────────────────────────
// SUBMENU COLUMN
// ──────────────────────────────────────────────────────────────
const SubmenuColumn = memo(({ column, onLinkClick }) => (
  <div className="sub-nav-title">
    <div className="submenu__column-label">{column.title}</div>
    <ul className="submenu__column-list">
      {column.items.map((item) => (
        <SubmenuLink key={item.label} item={item} onClick={onLinkClick} />
      ))}
    </ul>
  </div>
));
SubmenuColumn.displayName = "SubmenuColumn";

// ──────────────────────────────────────────────────────────────
// ANIMATION PHASES
//   idle     → not mounted
//   entering → mounted, fading in
//   visible  → fully visible
//   leaving  → fading out, then unmount
// ──────────────────────────────────────────────────────────────
export default function NavigationSubmenu({ isOpen, columns, submenuId, onLinkClick }) {
  const [displayColumns, setDisplayColumns] = useState(null);
  const [phase, setPhase] = useState("idle");

  const nextRef  = useRef(null);
  const phaseRef = useRef("idle");

  const setPhaseSync = (p) => { phaseRef.current = p; setPhase(p); };

  useEffect(() => {
    if (isOpen && columns) {
      if (phaseRef.current === "idle") {
        // ① Plain open
        nextRef.current = null;
        setDisplayColumns(columns);
        setPhaseSync("entering");
      } else if (phaseRef.current === "entering" || phaseRef.current === "visible") {
        // ② Switch — instant content swap, no animation between tabs
        nextRef.current = null;
        setDisplayColumns(columns);
      } else if (phaseRef.current === "leaving") {
        // ③ Switch while closing → reopen with new content
        nextRef.current = null;
        setDisplayColumns(columns);
        setPhaseSync("entering");
      }
    } else if (!isOpen) {
      if (phaseRef.current !== "idle") {
        nextRef.current = null;
        setPhaseSync("leaving");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submenuId]);

  const handleTransitionEnd = useCallback((e) => {
    if (e.propertyName !== "opacity") return;
    if (phaseRef.current === "leaving") {
      setDisplayColumns(null);
      setPhaseSync("idle");
    }
  }, []);

  useEffect(() => {
    if (phase === "entering") {
      const id = requestAnimationFrame(() =>
        requestAnimationFrame(() => setPhaseSync("visible"))
      );
      return () => cancelAnimationFrame(id);
    }
  }, [phase]);

  if (phase === "idle" || !displayColumns) return null;

  const colCount  = Math.min(displayColumns.length, 3);
  const isVisible = phase === "visible";

  return (
    <nav
      className={`sub-navs${isVisible ? " sub-navs--visible" : ""}`}
      aria-label="القائمة الفرعية"
      onTransitionEnd={handleTransitionEnd}
    >
      <div className={`submenu__content submenu__content--cols-${colCount}`}>
        {displayColumns.map((column, index) => (
          <SubmenuColumn key={column.title || index} column={column} onLinkClick={onLinkClick} />
        ))}
      </div>
    </nav>
  );
}
